/** English strings for the app's domains area. Keys are camelCase, grouped by screen. */
import { plural } from "../plural";

export default {
  /** Status badges. The status values the code compares stay in English. */
  status: {
    active: "Active",
    awaitingDns: "Awaiting DNS",
    transferredOut: "Transferred out",
    registering: "Registering…",
    failed: "Failed",
  },
  /** "Renews …" fragments after a bought domain's details. */
  renewal: {
    paid: "Renewal paid",
    wontRenew: "Won't renew",
    theRenewalPrice: "the renewal price",
    chargedAround: "Auto-renew: {price}/yr, charged around {date}",
    chargedBeforeExpiry: "Auto-renew: {price}/yr, charged before it expires",
  },
  page: {
    title: "Domains",
    subtitleSell: "Buy a domain or connect one you already own.",
    subtitleConnect: "Connect a domain you already own.",
    yourDomains: "Your domains",
    addDomainHeading: "Add a domain",
    getDomainHeading: "Get a domain",
    connectDomainHeading: "Connect a domain",
    buyHint: "Search availability and check out in one step.",
    connectHint: "Point a domain you already own at Mythex.",
    modeSwitch: "Domain action",
    modeBuy: "Buy",
    proOnly: "Custom domains are available on Pro.",
    emptySell: "Your purchased and connected domains will show up here.",
    emptyConnect: "Domains you connect will show up here.",
    transferredOutHeading: "Transferred out",
    transferredOutHint:
      "No longer managed by Mythex. Auth codes stay available until you finish the transfer.",
    viewAuthCode: "View auth code",
    projectFallback: "Project",
  },
  buy: {
    searchPlaceholder: "Search name or name.tld…",
    searching: "Searching…",
    connectToProject: "Connect to project <muted>(optional)</muted>",
    connectToProjectLabel: "Connect to project",
    buyOnly: "Buy only — connect later",
    available: plural({ one: "{n} available", other: "{n} available" }),
    perYear: "{price}/yr",
    buy: "Buy",
    premium: "Premium",
    unavailable: "Unavailable",
    taken: "Taken",
    tryName: "Try a name like {short} or {full}",
  },
  connect: {
    project: "Project",
    selectProject: "Select a project…",
    hostname: "Hostname",
    pointsAt: "Points at",
    serviceLabel: "Which service this domain serves",
    serviceHint:
      "This domain becomes that service's own public address. Your site keeps reaching it at /api either way.",
    siteHint: "The site, including everything it serves at /api.",
    rootWebsiteOnly: "A root domain always points at your website. To give your API its own address, use a subdomain like {example}.",
    cnameAfter: "We’ll show the exact DNS records to add after you connect — then {verify} here.",
    adding: "Adding…",
    addDomain: "Add domain",
  },
  row: {
    connectedLine: "Connected · {project}",
    pairAddRecords: "At your DNS provider, add these 2 records and delete any other A or AAAA records on @. Then press {verify}.",
    pairIssuing: "Both records are in place. The SSL certificate is being issued — press {verify} again in a minute.",
    recordStatus: "Status",
    recordFound: "Found",
    recordNotFound: "Not found yet",
    pairVerifyHint: "Checks both. DNS changes can take a few minutes to show.",
    subAddRecord: "At your DNS provider, add this record. Then press {verify}.",
    subIssuing: "The record is in place. The SSL certificate is being issued — press {verify} again in a minute.",
    subVerifyHint: "DNS changes can take a few minutes to show.",
    cnameTarget: "CNAME → {target}",
    copyCname: "Copy CNAME",
    verifyDns: "Verify DNS",
    primary: "Primary",
    imported: "Imported",
    expires: "Expires {date}",
    notConnected: "Not connected to a project",
    autoRenew: "Auto-renew",
    renewNow: "Renew now",
    changeProject: "Change project…",
    connectToProject: "Connect to project…",
    connectAria: "Connect {hostname} to a project",
  },
  menu: {
    label: "Domain actions",
    openProject: "Open project",
    removeDomain: "Remove domain",
    configure: "Configure",
    renewNowPrice: "Renew now · {price}",
    turnOffAutoRenew: "Turn off auto-renew",
    turnOnAutoRenew: "Turn on auto-renew",
    setPrimary: "Set as primary",
    disconnect: "Disconnect from project",
    transferOut: "Transfer out…",
  },
  transfer: {
    confirmTitle: "Transfer domain out?",
    confirmSubtitle:
      "We’ll unlock {hostname} and show an auth code. Save that code — you’ll need it at your new registrar to keep ownership.",
    confirmLabel: "Transfer out",
    authTitle: "Save your transfer auth code",
    authSubtitle: "{hostname} is unlocked for transfer. Paste this code at your new registrar.",
    authWarning:
      "Don’t close without saving — this code proves ownership for the transfer. You can also reopen it anytime under <em>Transferred out</em>.",
    savedIt: "I’ve saved it",
  },
  configure: {
    title: "Configure {hostname}",
    titleFallback: "Configure domain",
    subtitle: "DNS records, nameservers, and registration.",
    registration: "Registration",
    importedDns: "Imported · DNS on Mythex",
    boughtVia: "Bought via Mythex",
    whoisOff: "Turn off WHOIS privacy",
    whoisOn: "Turn on WHOIS privacy",
    project: "Project",
    projectHint: "Connect this domain to a published project so it serves that app.",
    nameservers: "Nameservers",
    nameserversHint:
      "Cloudflare nameservers (read-only). To use other nameservers, transfer the domain out.",
    dnsRecords: "DNS records",
    dnsHint: "Add MX, TXT, and other records. Locked rows are managed by Mythex for your site.",
    colType: "Type",
    colName: "Name",
    colValue: "Value",
    locked: " · locked",
    noRecords: "No records yet.",
    recordType: "Record type",
    namePlaceholder: "Name (@ or www)",
    valuePlaceholder: "Value",
    priorityPlaceholder: "Priority",
    deleteTitle: "Delete DNS record?",
  },
  toast: {
    buyingIsPro: "Buying domains is on Pro.",
    noSuggestions: "No suggestions — try another name.",
    searchNotConfigured: "Domain search isn’t configured right now.",
    searchFailed: "Couldn't search domains.",
    ready: "{hostname} is ready.",
    unavailable: "That domain isn't available.",
    premiumUnsupported: "Premium domains aren't supported yet.",
    billingDisabled: "Billing isn't configured yet.",
    buyFailed: "Couldn't buy that domain.",
    renewalPaid: "Renewal paid.",
    paymentReceived: "Payment received — registering your domain.",
    chooseProjectAndDomain: "Choose a project and enter a domain.",
    added: "Domain added — copy the CNAME, then Verify.",
    proRequired: "Custom domains are on Pro.",
    publishFirst: "Publish that project first.",
    hostnameTaken: "That domain is already in use.",
    unknownService: "That app doesn't publish that service any more.",
    invalidHostname: "Enter a valid domain like app.example.com.",
    notConfigured: "Custom domains aren’t configured on this environment yet.",
    addFailed: "Couldn't add that domain.",
    verified: "Domain verified — it’s live.",
    dnsPending: "DNS not ready yet — add the CNAME, wait a minute, then Verify again.",
    verifyFailed: "Couldn't verify yet. Check the CNAME and try again.",
    removed: "Domain removed.",
    removeFailed: "Couldn't remove that domain.",
    cnameCopied: "CNAME copied.",
    copyFailed: "Couldn't copy — select the text manually.",
    autoRenewOn: "Auto-renew on.",
    autoRenewOffAlreadyRenewed: "Auto-renew off. This year's renewal had already gone through.",
    autoRenewOff: "Auto-renew off.",
    autoRenewAtRegistrar: "Auto-renew is managed at your domain registrar.",
    autoRenewFailed: "Couldn't update auto-renew.",
    renewFailed: "Couldn't start the renewal.",
    primaryUpdated: "Primary domain updated.",
    primaryFailed: "Couldn't set primary domain.",
    disconnected: "Disconnected from project.",
    disconnectFailed: "Couldn't disconnect.",
    chooseProject: "Choose a project.",
    connected: "Domain connected.",
    connectFailed: "Couldn't connect.",
    transferStarted:
      "Transfer started. If you need an auth code, check Transferred out or your registrar email.",
    transferAtRegistrar: "Transfer this domain at your current registrar.",
    transferFailed: "Couldn't start transfer-out.",
    authCodeCopied: "Auth code copied.",
    copyCodeFailed: "Couldn't copy — select the code manually.",
    loadFailed: "Couldn't load domain details.",
    enterValue: "Enter a record value.",
    recordAdded: "DNS record added.",
    addRecordFailed: "Couldn't add DNS record.",
    recordDeleted: "DNS record deleted.",
    recordLocked: "That record is managed by Mythex.",
    deleteFailed: "Couldn't delete.",
    whoisOff: "WHOIS privacy off.",
    whoisOn: "WHOIS privacy on.",
    whoisFailed: "Couldn't update WHOIS privacy.",
  },
};
